import WebViewBridge, { WebViewBridgeProtocol } from '@flipster/webview-bridge';

import type { AbstractSocketEvents, SocketState } from './AbstractSocket';
import AutoReconnectingSocket from './AutoReconnectingSocket';

export interface BridgeSocketEvents<AppMessage, AppMetadata> extends AbstractSocketEvents<AppMessage> {
  metadata: AppMetadata;
  connectionError: Error;
}

export default class BridgeSocket<
  AppMessage = unknown,
  WebMessage = unknown,
  AppMetadata = unknown,
  WebMetadata = unknown,
  E extends BridgeSocketEvents<AppMessage, AppMetadata> = BridgeSocketEvents<AppMessage, AppMetadata>,
> extends AutoReconnectingSocket<WebMessage, E> {
  #bridge: WebViewBridge<AppMessage, WebMessage, AppMetadata, WebMetadata>;
  #protocol: WebViewBridgeProtocol<AppMessage, WebMessage, AppMetadata, WebMetadata>;
  #messageQueue: WebMessage[] = [];
  #appMetadata: AppMetadata | undefined;

  constructor(
    public readonly channelName: string,
    public readonly metadata: WebMetadata,
  ) {
    super();
    const onConnectionChanged = () => this.emit('stateChanged', this.state);
    const onMetadata = (_appMetadata: AppMetadata) => {
      const appMetadata = _appMetadata || ({} as AppMetadata);
      this.#appMetadata = appMetadata;
      this.emit('metadata', appMetadata);
    };
    const onMessage = (message: AppMessage) => this.emit('message', message);
    const onConnectionError = (error: Error) => this.emit('connectionError', error);
    const onPause = () => void (this.needReconnection = false);
    const onResume = () => void (this.needReconnection = true);
    this.#protocol = {
      channelName,
      metadata,
      onMetadata,
      onConnectionChanged,
      onMessage,
      onPause,
      onResume,
      onConnectionError,
      sendMessage: null,
    };
    this.#bridge = new WebViewBridge(this.#protocol);
  }

  get appMetadata(): AppMetadata | undefined {
    return this.#appMetadata;
  }

  get protocol() {
    return this.#protocol;
  }

  get debugLabel(): string {
    return this.#protocol.channelName;
  }

  get state(): SocketState {
    switch (this.#bridge.currentState) {
      case 'CONNECTING':
        return 'connecting';
      case 'CONNECTED':
        return 'connected';
      case 'DISCONNECTED':
      default:
        return 'closed';
    }
  }

  async tryConnect() {
    if (this.state !== 'closed') return super.tryConnect();
    await this.#bridge.start();
  }

  close() {
    super.close();
    this.#bridge.stop();
  }

  send(data: WebMessage): void {
    if (this.state === 'connected' && this.#protocol.sendMessage) {
      this.#protocol.sendMessage(data);
    } else {
      this.#messageQueue.push(data);
    }
  }

  protected clearQueuedMessages(): void {
    this.#messageQueue = [];
  }

  protected flushQueuedMessages(): void {
    if (!this.#protocol.sendMessage) return;
    this.#messageQueue.forEach((data) => this.send(data));
    this.clearQueuedMessages();
  }
}
